import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authClient } from "../lib/auth-client";
import { AuthError } from "../lib/auth-errors";
import { clearSwrCache } from "../lib/swr-cache";
import { PLAN_TYPES, type PlanType } from "../types";

interface User {
  id: string;
  email: string;
  role: string;
  plan: PlanType;
  name?: string;
  image?: string | null;
  emailVerified?: boolean;
  onboardingCompletedAt?: number | null;
  proOnboardingCompletedAt?: number | null;
}

// Better Auth レスポンスのユーザー型（role を含む）
interface AuthResponseUser {
  id: string;
  email: string;
  role?: string;
  plan?: string;
  name?: string;
  image?: string | null;
  emailVerified?: boolean;
  onboardingCompletedAt?: number | null;
  proOnboardingCompletedAt?: number | null;
}

/** API レスポンスの plan 文字列を PlanType に変換（不正値は "free" にフォールバック） */
function toPlanType(plan: string | undefined): PlanType {
  return plan && (PLAN_TYPES as readonly string[]).includes(plan) ? (plan as PlanType) : "free";
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** 初回認証チェックが完了したか。register/login の進行中は変化しない */
  isReady: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearAuth: () => void;
  updateUser: (userData: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isReady: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await authClient.signIn.email({
            email,
            password,
          });

          if (response.error) {
            throw new AuthError(
              response.error.code ?? "UNKNOWN",
              response.error.message ?? "Sign in failed",
            );
          }

          if (!response.data?.user) {
            throw new AuthError("UNKNOWN", "ログインレスポンスが不正です");
          }

          // 前アカウントの SWR キャッシュ（お気に入り状態を含むサービス一覧等）を持ち越さない
          clearSwrCache();

          const user = response.data.user as AuthResponseUser;
          set({
            user: {
              id: user.id,
              email: user.email,
              role: user.role || "user",
              plan: toPlanType(user.plan),
              name: user.name,
              image: user.image,
              emailVerified: user.emailVerified,
              onboardingCompletedAt: user.onboardingCompletedAt,
              proOnboardingCompletedAt: user.proOnboardingCompletedAt,
            },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          console.error("[auth.login] error:", error);
          set({ isLoading: false, isAuthenticated: false, user: null });
          throw error;
        }
      },

      register: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await authClient.signUp.email({
            email,
            password,
            name: email.split("@")[0], // デフォルトで名前を設定
          });

          if (response.error) {
            throw new AuthError(
              response.error.code ?? "UNKNOWN",
              response.error.message ?? "Sign up failed",
            );
          }

          if (!response.data?.user) {
            throw new AuthError("UNKNOWN", "登録レスポンスが不正です");
          }

          // requireEmailVerification: false (auto sign-in 有効) の場合、Better Auth は
          // signup レスポンスに token を含めて session を返す。
          // requireEmailVerification: true の場合は token: null となるためメール認証待ち。
          const responseData = response.data as Record<string, unknown>;
          const hasSession = !!(responseData.token || responseData.session);

          if (hasSession) {
            // セッションがある場合のみログイン状態にする
            clearSwrCache();
            const user = response.data.user as AuthResponseUser;
            set({
              user: {
                id: user.id,
                email: user.email,
                role: user.role || "user",
                plan: toPlanType(user.plan),
                name: user.name,
                image: user.image,
                emailVerified: user.emailVerified,
                onboardingCompletedAt: user.onboardingCompletedAt,
              },
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // メール認証待ちの場合、ログイン状態にしない
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error("[auth.register] error:", error);
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authClient.signOut();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error) {
          // ログアウトエラーでも状態はクリア
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          throw error;
        } finally {
          // 成否によらずユーザーデータのキャッシュは破棄する
          clearSwrCache();
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const session = await authClient.getSession();

          if (!session.data?.user) {
            throw new Error("セッションが見つかりません");
          }

          const user = session.data.user as AuthResponseUser;
          set({
            user: {
              id: user.id,
              email: user.email,
              role: user.role || "user",
              plan: toPlanType(user.plan),
              name: user.name,
              image: user.image,
              emailVerified: user.emailVerified,
              onboardingCompletedAt: user.onboardingCompletedAt,
              proOnboardingCompletedAt: user.proOnboardingCompletedAt,
            },
            isAuthenticated: true,
            isLoading: false,
            isReady: true,
          });
        } catch (_error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isReady: true,
          });
        }
      },

      clearAuth: () => {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // login/register/logout と対称に、ユーザーデータのキャッシュも破棄する
        clearSwrCache();
      },

      updateUser: (userData: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        // isAuthenticated を除外: 毎回サーバー検証が必須になる
      }),
    },
  ),
);
